// Which products the signed-in person's organisation holds (Independent Stays
// plan IS1, lobbero_be/src/products/flags.js). The server sends
// `user.products = { desk, stays }` on every session payload; the permission
// list it sends is already masked by it, so this file only decides which SHELL
// to draw — it never grants anything.
//
// ABSENT MEANS DESK. A session minted by a server older than IS1 carries no
// `products`, and every organisation before IS1 had Desk, so a missing value
// must draw the shell exactly as before.

export interface OrgProducts {
  desk: boolean;
  stays: boolean;
}

/** 'buildings' (Independent Stays IS2): CORE data a Desk organisation sees as
 *  Buildings and Units; an organisation without Desk sees the same rows as
 *  Properties inside Stays, so the Buildings and Units rail entries are hidden
 *  there. One rule: shown only with Desk, like 'desk'. */
export type ProductKey = 'core' | 'desk' | 'stays' | 'buildings';

type WithProducts = { products?: OrgProducts | null; role?: string | null } | null | undefined;

/* A CO-HOST (Independent Stays IS5) works on the Stays listings shared with
   them and nothing else, whatever their organisation holds: their shell is the
   Stays-only shell (no Desk home, no Main Menu, no Buildings). The server
   refuses every Desk request of theirs regardless (no permission). */
const COHOST_ROLE = 'stays_cohost';
const cohostShell = (user: WithProducts): boolean => user?.role === COHOST_ROLE;

export const deskInPlan = (user: WithProducts): boolean =>
  !cohostShell(user) && user?.products?.desk !== false;

export const staysOnlyAccount = (user: WithProducts): boolean =>
  cohostShell(user) || user?.products?.desk === false;

/** The products the SHELL is drawn for: the organisation's, except a co-host's
 *  (IS5), which is Stays only. */
export const shellProducts = (user: WithProducts): OrgProducts | null | undefined =>
  cohostShell(user) ? { desk: false, stays: true } : user?.products;

/** Where a Stays-only owner or manager lands, and where every Desk page sends them. */
export const STAYS_HOME = '/dashboard/stays';

/* The /dashboard sections that are Lobbero Desk. A Stays-only person typing one
   of these is sent to Stays (the API would refuse every request behind them with
   DESK_NOT_IN_PLAN anyway). '/dashboard' itself is the Desk home. */
const DESK_DASHBOARD_SECTIONS = [
  '/dashboard/home',
  '/dashboard/reports',
  '/dashboard/view-edit-export',
  '/dashboard/analytics',
  '/dashboard/residents',
  '/dashboard/tenants',
  '/dashboard/roster',
  '/dashboard/shifts',
  '/dashboard/staff-access',
  '/dashboard/visitor-passes',
];

export const isDeskDashboardHome = (path: string): boolean =>
  path === '/dashboard' || path === '/dashboard/';

export const isDeskDashboardPath = (path: string): boolean =>
  isDeskDashboardHome(path) ||
  DESK_DASHBOARD_SECTIONS.some(p => path === p || path.startsWith(`${p}/`));

/* The Buildings and Units screens ('buildings' product, IS2). Without Desk the
   same rows are the account's properties in Stays, so a typed address (a list,
   a unit, add or edit) opens Properties instead — screens open only where they
   apply (user, 2026-10-29). Not Desk: the API still serves these rows to Stays. */
const BUILDING_DASHBOARD_SECTIONS = ['/dashboard/buildings', '/dashboard/units'];

export const STAYS_PROPERTIES = `${STAYS_HOME}/properties`;

export const isBuildingsDashboardPath = (path: string): boolean =>
  BUILDING_DASHBOARD_SECTIONS.some(p => path === p || path.startsWith(`${p}/`));

/* The front-desk screens outside /dashboard (the desk shell, the shift monitor,
   the IQ handover). Gated on Desk as a product in the router. */
const DESK_SCREENS = [
  '/main-menu',
  '/visitor-log',
  '/daily-log',
  '/view-report',
  '/roster',
  '/parcels',
  '/visitor-exit',
  '/daily-log-exit',
  '/iq-handover',
  '/shift-monitor',
];

export const isDeskScreen = (path: string): boolean =>
  DESK_SCREENS.some(p => path === p || path.startsWith(`${p}/`));

export const DESK_NOT_IN_PLAN_REASON =
  'Lobbero Desk is not part of your plan. Here is Stays.';

/* R-IS1.10 (mirrors lobbero_be/src/products/rules.js): a concierge works a front
   desk and a resident lives in a building the desk runs, so neither can be
   added to an organisation without Lobbero Desk. UI-only — the server refuses
   with 409 DESK_NOT_IN_PLAN regardless. IS9: an organisation with a HOTEL may
   add a concierge, shown as "Reception" (BE src/products/rules.js roleRefusalFor). */
const DESK_ONLY_ROLES: readonly string[] = ['concierge', 'resident'];

export const rolesInPlan = <R extends string>(
  roles: readonly R[],
  user: WithProducts,
  { hotel = false }: { hotel?: boolean } = {}
): R[] =>
  deskInPlan(user)
    ? [...roles]
    : roles.filter(role => !DESK_ONLY_ROLES.includes(role) || (hotel && role === 'concierge'));

/** IS9: the concierge role is "Reception" in an organisation without Desk. */
export const roleLabelInPlan = (role: string | null | undefined, user: WithProducts, label: string): string =>
  !deskInPlan(user) && role === 'concierge' ? 'Reception' : label;
